import React, { useEffect } from 'react';

import Loading from '../Loading/Loading';
import Show from '../Show/Show';
import VideoConf from '../VideoConf/VideoConf';
import { GET_INSTANT_HELP_VIDEO } from '../../state/queries/InstantHelp';
import { captureException } from '@sentry/react-native';
import { client } from '../../config/apollo/client';
import { concatPath } from '../../utils/links/concatPath';
import { logAmplitude } from '../../utils/analytics/analytics';
import { makeRequestId } from '../../utils/others/makeRequestId';
import { observer } from 'mobx-react';
import { router } from 'expo-router';
import { tryAgainKey } from '../../constants/routeKeys';
import { useOnMount } from '../../hooks/useOnMount';
import { useStores } from '../../hooks/useStores';


interface GetInstantHelpVideoDetails {
  getInstantHelpVideoDetails: {
    id: number;
    message?: string;
    connectWith?: {
      openTokKey: string;
      openTokToken: string;
      openTokSessionId: string;
    }
  };
}

const goToTryAgain = (reason: string, activeCallId?: number) => {
  const params: { reason: string; activeCallId?: string } = { reason };
  if (activeCallId) {
    params.activeCallId = encodeURIComponent(activeCallId);
  }

  router.navigate({
    pathname: concatPath([tryAgainKey],0) as string,
    params
  });
};


const getConnectionDetails = async (
  userId: string,
  phoneNumber: string,
  region?: string | null,
  language?: string | null
) => {


  try{
    const {data: connectionDetailsData} = await client.query<GetInstantHelpVideoDetails>({ query: GET_INSTANT_HELP_VIDEO,
      variables: {
        input: {
          phoneNumber,
          region,
          language,
          requestId: makeRequestId({fn: 'getInstantHelpVideoDetails', userId})
        }
      },
      fetchPolicy: 'no-cache',
      context: {
        // WPO timeout
        timeout: 60000,
      }
    });
    return connectionDetailsData?.getInstantHelpVideoDetails;
  } catch(error) {
    console.error('Error while fetching connection details', error);
    goToTryAgain('errorInWPOApi');

    captureException(error);
  }
};



export const InstantHelpVideo  = observer(({timeStamp}: {timeStamp: number}) => {
  const { user, language: languageStore} = useStores();
  const region = user.user?.region;
  const language = languageStore?.languageWPO || 'fr';
  const [connectionDetails, setConnectionDetails] = React.useState<GetInstantHelpVideoDetails['getInstantHelpVideoDetails']>();
  const [loading, setLoading] = React.useState<boolean>();
  // timeStamp is an ugle workaround to fix a bug where this page would be balnk due to connectionDetails not being updated. Usefocusweffect is called to many times
  const phoneNumber = user?.phoneNumber || '';

  if (!phoneNumber || phoneNumber.length === 0) {
    captureException('No phone number provided for videocall');
  }



  useOnMount(() => {
    logAmplitude('Instant Help Video Page Opened');
  });

  useEffect(() => {
    console.log('InstantHelpVideo.loading', loading);
  }, [loading]);

  useEffect(() => {
    console.log('InstantHelpVideo.useEffect');
    if(phoneNumber) {
      const idempotencyId = `${user.user?._id || phoneNumber}`;
      console.log('InstantHelpVideo.useEffect.fetching');
      setConnectionDetails(undefined);
      setLoading(true);
      getConnectionDetails(idempotencyId, phoneNumber, region, language).then(result => {
        console.log('InstantHelpVideo.useEffect.gotResult');
        setLoading(false);
        setConnectionDetails(result);
      });
    }
  }, [phoneNumber, region, language, timeStamp, user.user?._id]);

  useEffect(() => {
    if(connectionDetails && !connectionDetails?.connectWith && connectionDetails?.id) {
      setConnectionDetails(undefined);
      goToTryAgain('cannotCreateNewCallYet', connectionDetails.id);

    }
  }, [connectionDetails, connectionDetails?.connectWith, connectionDetails?.id]);

  return (
    <Show when={!loading} fallback={<Loading />}>
      <Show when={connectionDetails?.id} >
        <Show when={connectionDetails?.connectWith} keyed>
          {conn => (
            <VideoConf
              requestId={connectionDetails!.id}
              openTokKey={conn.openTokKey}
              openTokToken={conn.openTokToken}
              openTokSessionId={conn.openTokSessionId}
            />
          )}
        </Show>
      </Show>
    </Show>

  );
});

